import { useState } from 'react'
import { Button } from '@/common/ui/button'
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
} from '@/common/ui/dialog'
import { Badge } from '@/common/ui/badge'
import { Eye } from 'lucide-react'

import type { GetBlacklistResponse } from '../../../../../type'

interface BlacklistViewReasonsDialogProps {
	item: GetBlacklistResponse['data'][number]
}

export const BlacklistViewReasonsDialog = ({
	item,
}: BlacklistViewReasonsDialogProps) => {
	const [open, setOpen] = useState(false)

	const { reasons, totalReport, accountNumber, accountName, bankProviderName } =
		item

	return (
		<>
			<Button
				variant="ghost"
				size="sm"
				onClick={() => setOpen(true)}
				className="h-auto p-0 font-normal"
			>
				<Eye className="mr-1 h-3 w-3" />
				<span className="max-w-xs truncate">
					{reasons && reasons.length > 0 ? reasons[0] : '-'}
				</span>
				{reasons && reasons.length > 1 && (
					<Badge variant="secondary" className="ml-1 h-5 text-xs">
						+{reasons.length - 1}
					</Badge>
				)}
			</Button>

			<Dialog open={open} onOpenChange={setOpen}>
				<DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-[600px]">
					<DialogHeader>
						<DialogTitle>รายละเอียดเหตุผล Blacklist</DialogTitle>
					</DialogHeader>

					<div className="space-y-4">
						{/* Account Information */}
						<div className="rounded-lg border p-4">
							<h4 className="mb-3 font-medium">ข้อมูลบัญชี</h4>
							<div className="grid grid-cols-1 gap-3 md:grid-cols-2">
								<div>
									<label className="text-sm font-medium text-muted-foreground">
										เลขบัญชี
									</label>
									<div className="mt-1 font-mono text-sm">{accountNumber}</div>
								</div>
								<div>
									<label className="text-sm font-medium text-muted-foreground">
										ชื่อบัญชี
									</label>
									<div className="mt-1 text-sm">{accountName}</div>
								</div>
								<div>
									<label className="text-sm font-medium text-muted-foreground">
										ธนาคาร
									</label>
									<div className="mt-1">
										<Badge variant="outline" className="font-medium">
											{bankProviderName}
										</Badge>
									</div>
								</div>
								<div>
									<label className="text-sm font-medium text-muted-foreground">
										จำนวนการรายงาน
									</label>
									<div className="mt-1 text-sm font-medium">{totalReport}</div>
								</div>
							</div>
						</div>

						{/* Reasons List */}
						<div className="rounded-lg border p-4">
							<h4 className="mb-3 font-medium">
								เหตุผลทั้งหมด ({reasons?.length || 0} รายการ)
							</h4>
							{reasons && reasons.length > 0 ? (
								<div className="space-y-3">
									{reasons.map((reason: string, index: number) => (
										<div
											key={index}
											className="rounded-md border-l-4 border-l-orange-500 bg-orange-50 p-3 dark:bg-orange-950/20"
										>
											<div className="flex items-start gap-2">
												<Badge
													variant="outline"
													className="mt-0.5 shrink-0 text-xs"
												>
													#{index + 1}
												</Badge>
												<p className="text-sm leading-relaxed">{reason}</p>
											</div>
										</div>
									))}
								</div>
							) : (
								<div className="py-8 text-center">
									<p className="text-muted-foreground">
										ไม่มีเหตุผลที่บันทึกไว้
									</p>
								</div>
							)}
						</div>

						<div className="flex justify-end pt-4">
							<Button variant="outline" onClick={() => setOpen(false)}>
								ปิด
							</Button>
						</div>
					</div>
				</DialogContent>
			</Dialog>
		</>
	)
}
